/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import React from 'react';

interface LicenseStatusPillProps {
    status: 'Activated' | 'Unused' | 'AutoProvisioned';
    activationCount: number;
}

/**
 * License Status Pill Component
 * 
 * Displays a colored pill badge indicating license status with activation count.
 * - Activated: Green (#4CAF50)
 * - Unused: Gray (#9E9E9E)
 * - AutoProvisioned: Blue (#045891)
 * 
 * Provides accessibility via title tooltip and aria-label.
 */
const LicenseStatusPill: React.FC<LicenseStatusPillProps> = ({ status, activationCount }) => {
    let backgroundColor = '#9E9E9E'; // Default: Unused (gray)
    let displayText = '';

    switch (status) {
        case 'Activated':
            backgroundColor = '#4CAF50'; // Green
            displayText = `Activated · ${activationCount} activation${activationCount !== 1 ? 's' : ''}`;
            break;
        case 'Unused':
            backgroundColor = '#9E9E9E'; // Gray
            displayText = `Unused · ${activationCount} activations`;
            break;
        case 'AutoProvisioned':
            backgroundColor = '#045891'; // Blue (license blue)
            displayText = `AutoProvisioned · ${activationCount} activation${activationCount !== 1 ? 's' : ''}`;
            break;
        default:
            displayText = `${activationCount}`;
    }

    const pillStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: backgroundColor,
        color: 'white',
        padding: '2px 8px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: 500,
        minWidth: '22px',
        height: '18px',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        cursor: 'default',
        userSelect: 'none',
        flexShrink: 0
    };

    return (
        <span
            style={pillStyle}
            title={displayText}
            aria-label={`${status}: ${activationCount} activation${activationCount !== 1 ? 's' : ''}`}
        >
            {activationCount}
        </span>
    );
};

export default LicenseStatusPill;